import { useCallback, useEffect, useState } from 'react';
import { AtSign, Mail, Search, Share2 } from 'lucide-react';
import { useMarkInvitedMutation, useSearchQuery } from '../profiles/api';
import { Avatar } from '../profiles/avatar';
import { useMyInviteLinkQuery } from '../inviteLinks/api';
import { useInviteFriendMutation } from '../mail/api';
import { useStartMutation } from '../relationships/api';
import { apiErrorMessage } from '../letters/ui';
import { inviteShareText, whatsAppShareUrl, withVia, type Via } from './share';
import { track } from './track';

/** Each channel tags the link it hands out (`via=`), so an arrival says how it came. */
function shareText(handle: string, url: string, via: Via): string {
  return inviteShareText(handle, withVia(url, via));
}

/**
 * The ways to ask someone for a letter (onboarding plan): WhatsApp, the share
 * sheet, an email invite, or finding someone already here. Used by the
 * first-run tour, the Getting started card and the empty letters list.
 */
export function InviteChoices({
  handle,
  onStarted,
}: {
  handle: string;
  /** "Already on toiyp?" began a correspondence with this relationship. */
  onStarted: (relationshipId: string) => void;
}) {
  // Made as the choices open, so the WhatsApp tap can open the app straight
  // away (a window opened after an await is a blocked popup on phones).
  const { data: link, isError: linkFailed } = useMyInviteLinkQuery();
  const [markInvited] = useMarkInvitedMutation();
  const [open, setOpen] = useState<'email' | 'search' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const ready = Boolean(link);

  const onWhatsApp = useCallback(() => {
    if (!link) return;
    const text = shareText(handle, link.url, 'wa');
    track({ event: 'share_opened', channel: 'whatsapp' });
    void markInvited();
    window.open(whatsAppShareUrl(text), '_blank', 'noopener');
  }, [link, handle, markInvited]);

  const onShare = useCallback(async () => {
    if (!link) return;
    setMsg(null);
    if (typeof navigator.share === 'function') {
      const text = shareText(handle, link.url, 'sheet');
      track({ event: 'share_opened', channel: 'sheet' });
      try {
        await navigator.share({ title: 'toiyp', text });
        void markInvited();
        return;
      } catch (e) {
        // Closing the share sheet is a choice, not an error.
        if ((e as { name?: string }).name === 'AbortError') return;
      }
    }
    track({ event: 'share_opened', channel: 'copy' });
    const text = shareText(handle, link.url, 'copy');
    try {
      await navigator.clipboard.writeText(text);
      void markInvited();
      setMsg('Copied. Paste it wherever you talk to them.');
    } catch {
      setMsg(text);
    }
  }, [link, handle, markInvited]);

  return (
    <>
      <div className="first-run__choices">
        <button type="button" className="first-run__choice first-run__choice--wa" disabled={!ready} onClick={onWhatsApp}>
          <WhatsAppGlyph />
          Send on WhatsApp
        </button>
        <button type="button" className="first-run__choice" disabled={!ready} onClick={() => void onShare()}>
          <Share2 size={18} aria-hidden="true" />
          Share another way
        </button>
        <button
          type="button"
          className="first-run__choice"
          aria-expanded={open === 'email'}
          onClick={() => setOpen(open === 'email' ? null : 'email')}
        >
          <Mail size={18} aria-hidden="true" />
          Invite by email
        </button>
        {open === 'email' && <EmailInvite />}
        <button
          type="button"
          className="first-run__choice"
          aria-expanded={open === 'search'}
          onClick={() => setOpen(open === 'search' ? null : 'search')}
        >
          <AtSign size={18} aria-hidden="true" />
          Already on toiyp?
        </button>
        {open === 'search' && <FindSomeone onStarted={onStarted} />}
      </div>

      {linkFailed && <p className="form-error" role="alert">Could not make your invite link just now.</p>}
      {msg && (
        <p className="invite-friend__ok first-run__copied" role="status">
          {msg}
        </p>
      )}
    </>
  );
}

function EmailInvite() {
  const [invite, { isLoading }] = useInviteFriendMutation();
  const [markInvited] = useMarkInvitedMutation();
  const [email, setEmail] = useState('');
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const onSend = useCallback(async () => {
    setErr(null);
    setOk(null);
    try {
      const res = await invite({ email: email.trim() }).unwrap();
      track({ event: 'share_opened', channel: 'email' });
      void markInvited();
      setEmail('');
      setOk(`Invite sent to ${res.email}. When they join, they can write to you.`);
    } catch (e) {
      setErr(apiErrorMessage(e, 'Could not send the invite'));
    }
  }, [invite, email, markInvited]);

  return (
    <form
      className="first-run__panel"
      onSubmit={(e) => {
        e.preventDefault();
        void onSend();
      }}
    >
      <input
        className="field__input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="friend@gmail.com"
        aria-label="Their email address"
        maxLength={254}
        autoFocus
      />
      <button type="submit" className="btn btn--ghost" disabled={isLoading || !email.trim()}>
        {isLoading ? 'Sending…' : 'Send invite'}
      </button>
      {ok && <p className="invite-friend__ok" role="status">{ok}</p>}
      {err && <p className="form-error" role="alert">{err}</p>}
    </form>
  );
}

function FindSomeone({ onStarted }: { onStarted: (relationshipId: string) => void }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [start, { isLoading: starting }] = useStartMutation();
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim().replace(/^@+/, '')), 250);
    return () => clearTimeout(t);
  }, [query]);

  const q = debounced.length >= 2 ? debounced : '';
  const { data: results, isFetching } = useSearchQuery(q, { skip: !q });

  const onPick = useCallback(
    async (handle: string) => {
      setErr(null);
      try {
        onStarted((await start({ handle }).unwrap()).id);
      } catch (e) {
        setErr(apiErrorMessage(e, 'Could not start that conversation'));
      }
    },
    [start, onStarted],
  );

  return (
    <div className="first-run__panel">
      <div className="new-conv__field">
        <Search size={16} className="new-conv__field-icon" aria-hidden="true" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find them by @handle…"
          aria-label="Find someone by handle"
        />
      </div>
      {q && (
        <div className="new-conv__results">
          {isFetching && <p className="muted">Checking the address book…</p>}
          {!isFetching && results?.length === 0 && <p className="muted">No one by that name on our round. Check the spelling, or send them an invite.</p>}
          {(results ?? []).map((p) => (
            <button
              key={p.handle}
              type="button"
              className="new-conv__result"
              disabled={starting}
              onClick={() => void onPick(p.handle)}
            >
              <span className="new-conv__avatar">
                <Avatar name={p.displayName ?? p.handle} avatar={p.avatar ?? null} />
              </span>
              <span className="new-conv__info">
                <span className="new-conv__name">@{p.handle}</span>
                {p.displayName && <span className="muted">{p.displayName}</span>}
              </span>
              <span className="new-conv__go">{starting ? '…' : 'write'}</span>
            </button>
          ))}
        </div>
      )}
      {err && <p className="form-error" role="alert">{err}</p>}
    </div>
  );
}

/** WhatsApp's speech-bubble mark, drawn plainly (lucide has no brand icons). */
function WhatsAppGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2c0 1.3.9 2.5 1 2.7.1.2 1.8 2.8 4.4 3.9 1.6.7 2.3.8 3.1.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.6-.3Z" />
    </svg>
  );
}
